/**
 * @file        src/lib/insuranceFare.js
 * @description The insurer's own fare split, read ONE way for every insurance
 *              document this console prints — the standalone policy invoice
 *              (pages/insurance/InvoiceDoc) and the add-on invoice printed under
 *              a flight (pages/flight/InvoiceDoc's FlightInsuranceInvoiceDoc).
 *
 *              The supplier states the premium as `SP`, the tax on it as
 *              `SPGST`, and usually its halves as `CGST`/`SGST`. Those halves
 *              are the SPGST SPLIT, not extra charges — adding all four would
 *              double-bill the customer on paper.
 *
 *              WHY THIS IS A MODULE and not four inline reads: the GST Details
 *              table and the totals box on the same sheet are two statements of
 *              one tax. Read verbatim, a payload carrying SPGST with no halves
 *              printed "CGST 0.00 · SGST 0.00" beside "GST 892.38" — one
 *              document contradicting itself. The fallback below is the same one
 *              the flight invoice already applies to Management Fee GST
 *              (CLAUDE.md §13): when the supplier does not state the halves, an
 *              even 9/9 split is what the review path prints.
 * @author      Kinetika Labs
 * @created     2026-09-25
 * @changelog
 *   - 2026-09-25 Claude — Initial. Extracted from the two insurance invoice
 *     bodies, which carried identical inline reads and an identical hardcoded
 *     IGST of 0.
 */

const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * @param {object|null} fare the supplier's `policy.fare` / `details.fare` object
 * @returns {{premium:number, gst:number, cgst:number, sgst:number, igst:number, hasSplit:boolean}}
 *
 * `hasSplit` is false when the insurer never issued — the document then states
 * the charged total alone, never a guessed breakdown on a tax document.
 */
export function insuranceGstSplit(fare) {
  const f = fare || {};
  const premium = Number(f.SP) || 0;
  const gst = Number(f.SPGST) || 0;
  let cgst = Number(f.CGST) || 0;
  let sgst = Number(f.SGST) || 0;
  // Carried through rather than hardcoded to 0, which is what both documents
  // did: an interstate supply is stated as IGST, and printing 0 against a real
  // figure would understate the tax on a tax invoice. Absent (the normal case
  // today) it is 0, exactly as before.
  const igst = Number(f.IGST) || 0;

  // Only when the supplier gave us a total and NO head to put it under. The
  // remainder lands on SGST so the two halves sum to exactly `gst` — on an
  // invoice a lost paisa is a real defect, not rounding.
  if (gst > 0 && cgst === 0 && sgst === 0 && igst === 0) {
    cgst = r2(gst / 2);
    sgst = r2(gst - cgst);
  }

  return { premium, gst, cgst, sgst, igst, hasSplit: premium > 0 };
}

export default insuranceGstSplit;
